import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { eventsApi, unitsApi, employeesApi, unitIdsApi, authApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Calendar, MapPin, Users, 
  Truck, ImageIcon, FileCode, AlertCircle, FileSpreadsheet, Tag, Shield, KeyRound, UserCog
} from 'lucide-react';
import { toast } from 'sonner';

const UNIT_TYPES = [
  { value: 'mobile_response', label: 'Mobile Response', personnelCount: 3 },
  { value: 'supervisor', label: 'Supervisor', personnelCount: 1 },
  { value: 'shift_lead', label: 'Shift Lead', personnelCount: 1 },
];

export default function AdminPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [units, setUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [unitIdOptions, setUnitIdOptions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  // Event form state
  const [eventForm, setEventForm] = useState({
    id: null,
    name: '',
    location: '',
    description: '',
    map_type: 'none',
    map_data: '',
    medical_areas: ['', '', '', '']
  });
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  // Unit form state
  const [unitForm, setUnitForm] = useState({
    id: null,
    unit_id: '',
    unit_type: '',
    event_id: '',
    personnel: []
  });
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);

  // Unit ID form state
  const [unitIdForm, setUnitIdForm] = useState({
    unit_id: '',
    unit_type: ''
  });

  // Employee form state
  const [employeeForm, setEmployeeForm] = useState({ name: '', email: '', username: '' });
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);

  // User edit state
  const [editingUser, setEditingUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({ name: '', email: '' });
  const [userEditDialogOpen, setUserEditDialogOpen] = useState(false);

  // Add User state
  const [addUserForm, setAddUserForm] = useState({ name: '', username: '', role: 'dispatcher' });
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, unitsRes, employeesRes, unitIdsRes] = await Promise.all([
        eventsApi.getAll(),
        unitsApi.getAll(),
        employeesApi.getAll(),
        unitIdsApi.getAll(),
      ]);
      setEvents(eventsRes.data);
      setUnits(unitsRes.data);
      setEmployees(employeesRes.data);
      setUnitIdOptions(unitIdsRes.data);
      // Users tab is admin-only, fetch separately
      try {
        const usersRes = await authApi.getUsers();
        setAllUsers(usersRes.data);
      } catch (e) {
        // Non-admin users can't fetch users list - that's expected
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get required personnel count based on unit type
  const getPersonnelCount = (unitType) => {
    const type = UNIT_TYPES.find(t => t.value === unitType);
    return type ? type.personnelCount : 0;
  };

  // Get unit IDs filtered by type
  const getUnitIdsByType = (unitType) => {
    return unitIdOptions.filter(u => u.unit_type === unitType);
  };

  // Event handlers
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      if (eventForm.id) {
        await eventsApi.update(eventForm.id, eventForm);
        toast.success('Event updated');
      } else {
        await eventsApi.create(eventForm);
        toast.success('Event created');
      }
      setEventDialogOpen(false);
      resetEventForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save event');
      console.error(error);
    }
  };

  const handleEditEvent = (event) => {
    setEventForm({
      id: event.id,
      name: event.name,
      location: event.location,
      description: event.description || '',
      map_type: event.map_type || 'none',
      map_data: event.map_data || '',
      medical_areas: event.medical_areas || ['', '', '', '']
    });
    setEventDialogOpen(true);
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await eventsApi.delete(id);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
      console.error(error);
    }
  };

  const handleToggleEventActive = async (event) => {
    try {
      await eventsApi.update(event.id, { is_active: !event.is_active });
      toast.success(`Event ${event.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update event');
      console.error(error);
    }
  };

  const resetEventForm = () => {
    setEventForm({
      id: null,
      name: '',
      location: '',
      description: '',
      map_type: 'none',
      map_data: '',
      medical_areas: ['', '', '', '']
    });
  };

  // Unit handlers
  const handleUnitSubmit = async (e) => {
    e.preventDefault();
    try {
      const unitData = {
        unit_id: unitForm.unit_id,
        unit_type: unitForm.unit_type,
        event_id: unitForm.event_id === 'none' ? null : unitForm.event_id,
        personnel: unitForm.personnel.filter(p => p && p !== 'none')
      };

      if (unitForm.id) {
        await unitsApi.update(unitForm.id, unitData);
        toast.success('Unit updated');
      } else {
        await unitsApi.create(unitData);
        toast.success('Unit created');
      }
      setUnitDialogOpen(false);
      resetUnitForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save unit');
      console.error(error);
    }
  };

  const handleEditUnit = (unit) => {
    setUnitForm({
      id: unit.id,
      unit_id: unit.unit_id,
      unit_type: unit.unit_type,
      event_id: unit.event_id || 'none',
      personnel: unit.personnel || []
    });
    setUnitDialogOpen(true);
  };

  const handleDeleteUnit = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    try {
      await unitsApi.delete(id);
      toast.success('Unit deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete unit');
      console.error(error);
    }
  };

  const resetUnitForm = () => {
    setUnitForm({
      id: null,
      unit_id: '',
      unit_type: '',
      event_id: '',
      personnel: []
    });
  };

  // Handle unit type change
  const handleUnitTypeChange = (newType) => {
    const count = getPersonnelCount(newType);
    setUnitForm(prev => ({
      ...prev,
      unit_type: newType,
      unit_id: '', // Reset unit_id when type changes
      personnel: Array(count).fill('')
    }));
  };

  // Handle personnel selection change
  const handlePersonnelChange = (index, employeeId) => {
    setUnitForm(prev => {
      const newPersonnel = [...prev.personnel];
      newPersonnel[index] = employeeId === 'none' ? '' : employeeId;
      return { ...prev, personnel: newPersonnel };
    });
  };

  // Unit ID handlers
  const handleAddUnitId = async (e) => {
    e.preventDefault();
    if (!unitIdForm.unit_id.trim() || !unitIdForm.unit_type) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await unitIdsApi.create({
        unit_id: unitIdForm.unit_id.trim(),
        unit_type: unitIdForm.unit_type
      });
      toast.success('Unit ID added');
      setUnitIdForm({ unit_id: '', unit_type: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add Unit ID');
      console.error(error);
    }
  };

  const handleDeleteUnitId = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Unit ID?')) return;
    try {
      await unitIdsApi.delete(id);
      toast.success('Unit ID deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete Unit ID');
      console.error(error);
    }
  };

  // Employee handlers
  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    if (!employeeForm.name.trim()) return;
    
    try {
      if (editingEmployee) {
        await employeesApi.update(editingEmployee.id, employeeForm);
        toast.success('Employee updated');
      } else {
        await employeesApi.create(employeeForm);
        toast.success('Employee added' + (employeeForm.email ? ' (user account created with temp password: osmc2025)' : ''));
      }
      setEmployeeForm({ name: '', email: '', username: '' });
      setEditingEmployee(null);
      setEmployeeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save employee');
      console.error(error);
    }
  };

  const handleEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({ name: emp.name, email: emp.email || '', username: emp.username || '' });
    setEmployeeDialogOpen(true);
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Are you sure? This will also delete the associated user account.')) return;
    try {
      await employeesApi.delete(id);
      toast.success('Employee deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete employee');
      console.error(error);
    }
  };

  // User management handlers
  const handleEditUser = (u) => {
    setEditingUser(u);
    setUserEditForm({ name: u.name || '', email: u.email || '', phone_number: u.phone_number || '', role: u.role || 'dispatcher' });
    setUserEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await authApi.updateUser(editingUser.id, userEditForm);
      toast.success('User updated');
      setUserEditDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user account?')) return;
    try {
      await authApi.deleteUser(id);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (id) => {
    if (!window.confirm('Reset this user\'s password to osmc2025? They will be forced to change it on next login.')) return;
    try {
      await authApi.resetPassword(id);
      toast.success('Password reset to temporary password');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!addUserForm.name.trim() || !addUserForm.username.trim()) {
      toast.error('Name and username are required');
      return;
    }
    try {
      await authApi.createUser(addUserForm);
      toast.success(`User "${addUserForm.username}" created with temporary password osmc2025`);
      setAddUserDialogOpen(false);
      setAddUserForm({ name: '', username: '', role: 'dispatcher' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await employeesApi.uploadExcel(file);
      toast.success(result.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to upload file: ' + (error.response?.data?.detail || error.message));
      console.error(error);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // File upload handler for maps
  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (type === 'image') {
        setEventForm(prev => ({
          ...prev,
          map_type: 'image',
          map_data: event.target.result
        }));
      } else if (type === 'kml') {
        setEventForm(prev => ({
          ...prev,
          map_type: 'kml',
          map_data: event.target.result
        }));
      }
    };

    if (type === 'image') {
      reader.readAsDataURL(file);
    } else {
      reader.readAsText(file);
    }
  };

  // Get employee name by ID
  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? emp.name : 'Unknown';
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-zinc-950 p-6" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="font-heading text-3xl font-bold text-white mb-6">Admin Panel</h1>

        <Tabs defaultValue="events" className="space-y-6">
          <TabsList className="bg-zinc-800 border border-zinc-700">
            <TabsTrigger value="events" className="data-[state=active]:bg-blue-600" data-testid="events-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="unit-ids" className="data-[state=active]:bg-blue-600" data-testid="unit-ids-tab">
              <Tag className="w-4 h-4 mr-2" />
              Unit IDs
            </TabsTrigger>
            <TabsTrigger value="units" className="data-[state=active]:bg-blue-600" data-testid="units-tab">
              <Truck className="w-4 h-4 mr-2" />
              Units
            </TabsTrigger>
            <TabsTrigger value="employees" className="data-[state=active]:bg-blue-600" data-testid="employees-tab">
              <Users className="w-4 h-4 mr-2" />
              Employees
            </TabsTrigger>
            {isAdmin && (
            <TabsTrigger value="users" className="data-[state=active]:bg-blue-600" data-testid="users-tab">
              <UserCog className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            )}
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader className="border-b border-zinc-700 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl text-white">Events Management</CardTitle>
                <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={resetEventForm}
                      data-testid="create-event-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Event
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-white">
                        {eventForm.id ? 'Edit Event' : 'Create Event'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEventSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Event Name</Label>
                        <Input
                          value={eventForm.name}
                          onChange={(e) => setEventForm(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-zinc-800 border-zinc-600 text-white"
                          placeholder="Concert, Festival, Game..."
                          required
                          data-testid="event-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Location</Label>
                        <Input
                          value={eventForm.location}
                          onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                          className="bg-zinc-800 border-zinc-600 text-white"
                          placeholder="Venue name or address"
                          required
                          data-testid="event-location-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Description</Label>
                        <Textarea
                          value={eventForm.description}
                          onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                          className="bg-zinc-800 border-zinc-600 text-white"
                          placeholder="Event details..."
                          data-testid="event-description-input"
                        />
                      </div>
                      <div className="space-y-3 p-3 bg-zinc-800/50 rounded-sm border border-zinc-700">
                        <Label className="text-zinc-300 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-emerald-500" />
                          Medical Area Designations
                        </Label>
                        {[0, 1, 2, 3].map((index) => (
                          <div key={index} className="space-y-1">
                            <Label className="text-zinc-400 text-sm">Area {index + 1}</Label>
                            <Input
                              value={eventForm.medical_areas[index] || ''}
                              onChange={(e) => {
                                const newAreas = [...eventForm.medical_areas];
                                newAreas[index] = e.target.value;
                                setEventForm(prev => ({ ...prev, medical_areas: newAreas }));
                              }}
                              className="bg-zinc-800 border-zinc-600 text-white"
                              placeholder={`Medical Area ${index + 1} designation...`}
                              data-testid={`medical-area-input-${index}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Event Map</Label>
                        <div className="flex gap-2">
                          <label className="flex-1">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleFileUpload(e, 'image')}
                            />
                            <Button type="button" variant="outline" className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800" asChild>
                              <span>
                                <ImageIcon className="w-4 h-4 mr-2" />
                                Upload Image
                              </span>
                            </Button>
                          </label>
                          <label className="flex-1">
                            <input
                              type="file"
                              accept=".kml,.kmz"
                              className="hidden"
                              onChange={(e) => handleFileUpload(e, 'kml')}
                            />
                            <Button type="button" variant="outline" className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800" asChild>
                              <span>
                                <FileCode className="w-4 h-4 mr-2" />
                                Upload KML
                              </span>
                            </Button>
                          </label>
                        </div>
                        {eventForm.map_type !== 'none' && (
                          <Badge className="bg-emerald-500/20 text-emerald-500">
                            {eventForm.map_type === 'image' ? 'Image uploaded' : 'KML uploaded'}
                          </Badge>
                        )}
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button type="button" variant="outline" className="border-zinc-600 text-zinc-300">
                            Cancel
                          </Button>
                        </DialogClose>
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-event-button">
                          {eventForm.id ? 'Update' : 'Create'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-4">
                <ScrollArea className="h-[400px]">
                  {loading ? (
                    <p className="text-zinc-500">Loading...</p>
                  ) : events.length === 0 ? (
                    <p className="text-zinc-500">No events created yet</p>
                  ) : (
                    <div className="space-y-3">
                      {events.map(event => (
                        <div 
                          key={event.id}
                          className="bg-zinc-800 border border-zinc-700 p-4 rounded-sm flex items-center justify-between"
                          data-testid={`event-row-${event.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-white font-semibold">{event.name}</h3>
                              <Badge className={event.is_active ? 'bg-emerald-500/20 text-emerald-500' : 'bg-zinc-600 text-zinc-400'}>
                                {event.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            <p className="text-zinc-400 text-sm flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </p>
                            {event.medical_areas && event.medical_areas.some(a => a) && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {event.medical_areas.filter(a => a).map((area, i) => (
                                  <Badge key={i} className="bg-blue-500/15 text-blue-400 text-xs">
                                    {area}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Label className="text-zinc-400 text-sm">Active</Label>
                              <Switch
                                checked={event.is_active}
                                onCheckedChange={() => handleToggleEventActive(event)}
                              />
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-zinc-600 text-zinc-300"
                              onClick={() => handleEditEvent(event)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-red-500 text-red-500 hover:bg-red-500/10"
                              onClick={() => handleDeleteEvent(event.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Unit IDs Tab */}
          <TabsContent value="unit-ids">
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader className="border-b border-zinc-700">
                <CardTitle className="font-heading text-xl text-white">Unit IDs Management</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {/* Add Unit ID Form */}
                <form onSubmit={handleAddUnitId} className="flex gap-4 mb-6">
                  <Input
                    value={unitIdForm.unit_id}
                    onChange={(e) => setUnitIdForm(prev => ({ ...prev, unit_id: e.target.value }))}
                    className="bg-zinc-800 border-zinc-600 text-white w-40"
                    placeholder="Unit ID (e.g., M-1)"
                    data-testid="unit-id-create-input"
                  />
                  <Select 
                    value={unitIdForm.unit_type} 
                    onValueChange={(val) => setUnitIdForm(prev => ({ ...prev, unit_type: val }))}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white w-48" data-testid="unit-id-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-600">
                      {UNIT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value} className="text-white hover:bg-zinc-700">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="add-unit-id-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Unit ID
                  </Button>
                </form>

                <p className="text-zinc-500 text-sm mb-4">
                  Create Unit IDs that can be selected when creating units. Organize by type: Mobile Response, Supervisor, Shift Lead.
                </p>

                {/* Unit IDs List by Type */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {UNIT_TYPES.map(type => (
                    <div key={type.value} className="bg-zinc-800 border border-zinc-700 p-4 rounded-sm">
                      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <Tag className="w-4 h-4 text-blue-500" />
                        {type.label}
                      </h3>
                      <ScrollArea className="h-[200px]">
                        {getUnitIdsByType(type.value).length === 0 ? (
                          <p className="text-zinc-500 text-sm">No IDs created</p>
                        ) : (
                          <div className="space-y-2">
                            {getUnitIdsByType(type.value).map(uid => (
                              <div 
                                key={uid.id}
                                className="flex items-center justify-between bg-zinc-900 p-2 rounded"
                                data-testid={`unit-id-row-${uid.id}`}
                              >
                                <span className="text-white font-medium">{uid.unit_id}</span>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-500 hover:bg-red-500/10 h-7 w-7 p-0"
                                  onClick={() => handleDeleteUnitId(uid.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Units Tab */}
          <TabsContent value="units">
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader className="border-b border-zinc-700 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl text-white">Units Management</CardTitle>
                <Dialog open={unitDialogOpen} onOpenChange={setUnitDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={resetUnitForm}
                      data-testid="create-unit-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Unit
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-white">
                        {unitForm.id ? 'Edit Unit' : 'Create Unit'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUnitSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Unit Type</Label>
                        <Select 
                          value={unitForm.unit_type} 
                          onValueChange={handleUnitTypeChange}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid="unit-type-select">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-600">
                            {UNIT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value} className="text-white hover:bg-zinc-700">
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Unit ID Dropdown - only show after type is selected */}
                      {unitForm.unit_type && (
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Unit ID</Label>
                          <Select 
                            value={unitForm.unit_id} 
                            onValueChange={(val) => setUnitForm(prev => ({ ...prev, unit_id: val }))}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid="unit-id-select">
                              <SelectValue placeholder="Select Unit ID" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-600">
                              {getUnitIdsByType(unitForm.unit_type).length === 0 ? (
                                <div className="px-2 py-2 text-zinc-500 text-sm">
                                  No Unit IDs available. Create them in Unit IDs tab.
                                </div>
                              ) : (
                                getUnitIdsByType(unitForm.unit_type).map(uid => (
                                  <SelectItem key={uid.id} value={uid.unit_id} className="text-white hover:bg-zinc-700">
                                    {uid.unit_id}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Personnel Selection - Dynamic based on unit type */}
                      {unitForm.unit_type && (
                        <div className="space-y-3 p-3 bg-zinc-800/50 rounded-sm border border-zinc-700">
                          <Label className="text-zinc-300 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Personnel ({getPersonnelCount(unitForm.unit_type)} required)
                          </Label>
                          {Array.from({ length: getPersonnelCount(unitForm.unit_type) }).map((_, index) => (
                            <div key={index} className="space-y-1">
                              <Label className="text-zinc-400 text-sm">
                                {unitForm.unit_type === 'mobile_response' 
                                  ? `Team Member ${index + 1}` 
                                  : unitForm.unit_type === 'supervisor' 
                                    ? 'Supervisor' 
                                    : 'Shift Lead'}
                              </Label>
                              <Select 
                                value={unitForm.personnel[index] || 'none'} 
                                onValueChange={(val) => handlePersonnelChange(index, val)}
                              >
                                <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid={`personnel-select-${index}`}>
                                  <SelectValue placeholder="Select employee" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-600 max-h-[200px]">
                                  <SelectItem value="none" className="text-zinc-400">Select employee</SelectItem>
                                  {employees.map(emp => (
                                    <SelectItem key={emp.id} value={emp.id} className="text-white hover:bg-zinc-700">
                                      {emp.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                          {employees.length === 0 && (
                            <p className="text-amber-500 text-sm">No employees added yet. Go to Employees tab to add employees.</p>
                          )}
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-zinc-300">Assigned Event</Label>
                        <Select 
                          value={unitForm.event_id || "none"} 
                          onValueChange={(val) => setUnitForm(prev => ({ ...prev, event_id: val }))}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid="unit-event-select">
                            <SelectValue placeholder="Select event (optional)" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-600">
                            <SelectItem value="none" className="text-zinc-400">No Event</SelectItem>
                            {events.map(event => (
                              <SelectItem key={event.id} value={event.id} className="text-white hover:bg-zinc-700">
                                {event.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button type="button" variant="outline" className="border-zinc-600 text-zinc-300">
                            Cancel
                          </Button>
                        </DialogClose>
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-unit-button">
                          {unitForm.id ? 'Update' : 'Create'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-4">
                <ScrollArea className="h-[400px]">
                  {loading ? (
                    <p className="text-zinc-500">Loading...</p>
                  ) : units.length === 0 ? (
                    <p className="text-zinc-500">No units created yet</p>
                  ) : (
                    <div className="space-y-3">
                      {units.map(unit => {
                        const unitEvent = events.find(e => e.id === unit.event_id);
                        const unitTypeConfig = UNIT_TYPES.find(t => t.value === unit.unit_type);
                        return (
                          <div 
                            key={unit.id}
                            className="bg-zinc-800 border border-zinc-700 p-4 rounded-sm"
                            data-testid={`unit-row-${unit.id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className="text-blue-500 font-bold text-lg">{unit.unit_id}</span>
                                <Badge variant="outline" className="border-zinc-500 text-zinc-400">
                                  {unitTypeConfig?.label || unit.unit_type}
                                </Badge>
                                <Badge className={
                                  unit.status === 'available' ? 'bg-emerald-500/20 text-emerald-500' :
                                  unit.status === 'at_post' ? 'bg-gray-500/20 text-gray-400' :
                                  'bg-amber-500/20 text-amber-500'
                                }>
                                  {unit.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="border-zinc-600 text-zinc-300"
                                  onClick={() => handleEditUnit(unit)}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="border-red-500 text-red-500 hover:bg-red-500/10"
                                  onClick={() => handleDeleteUnit(unit.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm">
                              {unitEvent && (
                                <p className="text-zinc-400 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {unitEvent.name}
                                </p>
                              )}
                              {unit.personnel && unit.personnel.length > 0 && (
                                <p className="text-zinc-400 flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {unit.personnel.map(empId => getEmployeeName(empId)).join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees">
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader className="border-b border-zinc-700">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl text-white">Employees Management</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => { setEditingEmployee(null); setEmployeeForm({ name: '', email: '', username: '' }); setEmployeeDialogOpen(true); }}
                      data-testid="add-employee-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Employee
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                      onChange={handleExcelUpload}
                      data-testid="excel-upload-input"
                    />
                    <Button 
                      variant="outline" 
                      className="border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="upload-excel-button"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Upload Excel
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-zinc-500 text-sm mb-4">
                  Add employees individually or upload Excel/CSV. If email is provided, a user account is created with temp password <code className="text-amber-400">osmc2025</code> (forced reset on first login).
                </p>

                <ScrollArea className="h-[400px]">
                  {loading ? (
                    <p className="text-zinc-500">Loading...</p>
                  ) : employees.length === 0 ? (
                    <p className="text-zinc-500">No employees added yet</p>
                  ) : (
                    <div className="space-y-2">
                      {employees.map(emp => (
                        <div 
                          key={emp.id}
                          className="bg-zinc-800 border border-zinc-700 p-3 rounded-sm flex items-center justify-between"
                          data-testid={`employee-row-${emp.id}`}
                        >
                          <div>
                            <span className="text-white font-medium">{emp.name}</span>
                            <div className="flex gap-3 mt-1">
                              {emp.email && <span className="text-zinc-400 text-xs">{emp.email}</span>}
                              {emp.username && <span className="text-zinc-500 text-xs">@{emp.username}</span>}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-blue-400 hover:bg-blue-500/10 h-8 w-8 p-0"
                              onClick={() => handleEditEmployee(emp)}
                              data-testid={`edit-employee-${emp.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:bg-red-500/10 h-8 w-8 p-0"
                              onClick={() => handleDeleteEmployee(emp.id)}
                              data-testid={`delete-employee-${emp.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                <div className="mt-4 pt-4 border-t border-zinc-700">
                  <p className="text-zinc-400 text-sm">
                    Total Employees: <span className="text-white font-semibold">{employees.length}</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Employee Add/Edit Dialog */}
            <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
              <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEmployeeSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Name *</Label>
                    <Input
                      value={employeeForm.name}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-zinc-800 border-zinc-600 text-white"
                      placeholder="Full name"
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Email Address</Label>
                    <Input
                      type="email"
                      value={employeeForm.email}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-zinc-800 border-zinc-600 text-white"
                      placeholder="email@example.com (creates login account)"
                      data-testid="employee-email-input"
                    />
                    <p className="text-zinc-500 text-xs">Not required. If provided, a login account is created.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Username</Label>
                    <Input
                      value={employeeForm.username}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, username: e.target.value }))}
                      className="bg-zinc-800 border-zinc-600 text-white"
                      placeholder="Username"
                      data-testid="employee-username-input"
                    />
                  </div>
                  {!editingEmployee && employeeForm.email && (
                    <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-sm">
                      <p className="text-amber-400 text-sm flex items-center gap-2">
                        <KeyRound className="w-4 h-4" />
                        Temp password: <code className="font-bold">osmc2025</code>
                      </p>
                      <p className="text-zinc-400 text-xs mt-1">User will be forced to change on first login.</p>
                    </div>
                  )}
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline" className="border-zinc-600 text-zinc-300">Cancel</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      {editingEmployee ? 'Update' : 'Add Employee'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader className="border-b border-zinc-700">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    User Accounts
                  </CardTitle>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => { setAddUserForm({ name: '', username: '', role: 'dispatcher' }); setAddUserDialogOpen(true); }}
                    data-testid="add-user-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-zinc-500 text-sm mb-4">
                  Manage user login accounts. New users receive temporary password <span className="text-amber-400 font-mono">osmc2025</span> and must change it on first login.
                </p>

                <ScrollArea className="h-[400px]">
                  {loading ? (
                    <p className="text-zinc-500">Loading...</p>
                  ) : allUsers.length === 0 ? (
                    <p className="text-zinc-500">No user accounts found</p>
                  ) : (
                    <div className="space-y-2">
                      {allUsers.map(u => (
                        <div 
                          key={u.id}
                          className="bg-zinc-800 border border-zinc-700 p-3 rounded-sm flex items-center justify-between"
                          data-testid={`user-row-${u.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{u.name}</span>
                              <Badge className={u.role === 'admin' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}>
                                {u.role}
                              </Badge>
                              {u.must_reset_password && (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">Reset Required</Badge>
                              )}
                            </div>
                            <span className="text-zinc-400 text-sm block">{u.username ? `@${u.username}` : u.email}</span>
                            {u.phone_number && <span className="text-zinc-500 text-xs block">SMS: {u.phone_number}</span>}
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-blue-400 hover:bg-blue-500/10 h-8 w-8 p-0"
                              onClick={() => handleEditUser(u)}
                              title="Edit user"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-amber-400 hover:bg-amber-500/10 h-8 w-8 p-0"
                              onClick={() => handleResetPassword(u.id)}
                              title="Reset password"
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            {u.role !== 'admin' && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-500 hover:bg-red-500/10 h-8 w-8 p-0"
                                onClick={() => handleDeleteUser(u.id)}
                                title="Delete user"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                <div className="mt-4 pt-4 border-t border-zinc-700">
                  <p className="text-zinc-400 text-sm">
                    Total Users: <span className="text-white font-semibold">{allUsers.length}</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* User Edit Dialog */}
            <Dialog open={userEditDialogOpen} onOpenChange={setUserEditDialogOpen}>
              <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Edit User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateUser} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Name</Label>
                    <Input
                      value={userEditForm.name}
                      onChange={(e) => setUserEditForm(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-zinc-800 border-zinc-600 text-white"
                      data-testid="user-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Email</Label>
                    <Input
                      type="email"
                      value={userEditForm.email}
                      onChange={(e) => setUserEditForm(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-zinc-800 border-zinc-600 text-white"
                      data-testid="user-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Phone Number (for SMS notifications)</Label>
                    <Input
                      type="tel"
                      value={userEditForm.phone_number}
                      onChange={(e) => setUserEditForm(prev => ({ ...prev, phone_number: e.target.value }))}
                      placeholder="+13471234567"
                      className="bg-zinc-800 border-zinc-600 text-white"
                      data-testid="user-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Role</Label>
                    <Select value={userEditForm.role} onValueChange={(val) => setUserEditForm(prev => ({ ...prev, role: val }))}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        <SelectItem value="dispatcher" className="text-white hover:bg-zinc-700">Dispatcher</SelectItem>
                        <SelectItem value="admin" className="text-white hover:bg-zinc-700">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline" className="border-zinc-600 text-zinc-300">Cancel</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Update User</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Add User Dialog */}
            <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
              <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Add New User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddUser} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Full Name</Label>
                    <Input
                      value={addUserForm.name}
                      onChange={(e) => setAddUserForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="John Smith"
                      className="bg-zinc-800 border-zinc-600 text-white"
                      data-testid="add-user-name-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Username</Label>
                    <Input
                      value={addUserForm.username}
                      onChange={(e) => setAddUserForm(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="jsmith"
                      className="bg-zinc-800 border-zinc-600 text-white"
                      data-testid="add-user-username-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Role</Label>
                    <Select value={addUserForm.role} onValueChange={(val) => setAddUserForm(prev => ({ ...prev, role: val }))}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid="add-user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        <SelectItem value="dispatcher" className="text-white hover:bg-zinc-700">Dispatcher</SelectItem>
                        <SelectItem value="admin" className="text-white hover:bg-zinc-700">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3">
                    <p className="text-amber-400 text-sm">
                      Temporary password: <span className="font-mono font-bold">osmc2025</span>
                    </p>
                    <p className="text-zinc-400 text-xs mt-1">User will be required to change password on first login.</p>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline" className="border-zinc-600 text-zinc-300">Cancel</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-submit">Create User</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
